import * as React from 'react';

import { PageHeader } from '../../components/core/PageHeader';
import { useEffect, useMemo } from 'react';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import {
  CheckboxVisibility,
  ConstrainMode,
  Dropdown,
  IColumn,
  ICommandBarItemProps,
  IconButton,
  Label,
  Link,
  Panel,
  PanelType,
  PrimaryButton,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  TextField,
  Toggle,
} from '@fluentui/react';
import { Market } from '../../api/markets.types';
import { RootState } from '../../redux/types';
import { ROUTES } from '../../config/routes';
import { useNavigate } from 'react-router-dom';
import { TimezoneSelector } from '../core/TimezoneSelector';
import { MultiSelector } from '../../components/core/MultiSelector';
import { createMarket, deleteMarket } from '../../api/markets';
import { OkCancelDialog } from '../../components/core/OkCancelDialog';
import _ from 'lodash';

function updateArray<T>(arr: T[], index: number, update: (item: T) => void): T[] {
  const newArray = arr.slice();
  newArray[index] = {
    ...arr[index],
  };
  update(newArray[index]);
  return newArray;
}

export const Markets = () => {
  const dispatch = useDispatch();
  const [openCreate, setOpenCreate] = useState(false);
  const [hideInactive, setHideInactive] = useState(true);

  useEffect(() => {
    dispatch(onPageDidMount('Markets'));
    return () => {
      dispatch(onPageWillUnmount('Markets'));
    };
  }, [dispatch]);

  const actions: { items: ICommandBarItemProps[] } = { items: [] };
  actions.items.push({
    key: 'active',
    name: hideInactive ? 'Only active' : 'All',
    iconProps: { iconName: hideInactive ? 'FilterSolid' : 'Filter' },
    onClick: () => setHideInactive(!hideInactive),
  });
  actions.items.push({
    key: 'createMarket',
    name: 'Create market',
    iconProps: { iconName: 'Add' },
    onClick: () => setOpenCreate(true),
  });

  return (
    <>
      <PageHeader title="Markets" actions={actions} />
      <MarketDefinitions hideInactive={hideInactive} />
      <Panel isOpen={openCreate} onDismiss={() => setOpenCreate(false)} type={PanelType.medium} headerText="Create Market">
        <CreateMarket onCreate={() => setOpenCreate(false)} />
      </Panel>
    </>
  );
};

const CreateMarket = ({ onCreate }: { onCreate: () => void }) => {
  const [marketName, setMarketName] = useState('');
  const [marketCode, setMarketCode] = useState('');
  const [marketGroup, setMarketGroup] = useState('');
  const [timezone, setTimezone] = useState('');
  const [services, setServices] = useState<{ serviceCode: string; price: number; visibleToFollowUp: boolean; visibleToPatient: boolean }[]>([]);
  const [serviceCodes, setServiceCodes] = useState<string[]>([]);
  const [payerCodes, setPayerCodes] = useState<string[]>([]);
  const [yelpPage, setYelpPage] = useState('');
  const [region, setRegion] = useState('');

  const allServices = useSelector((state: RootState) => state.serviceOffering.services.data || []);
  const allPayers = useSelector((state: RootState) => state.visits.visitConfig.data?.payers || []);
  const markets = useSelector((state: RootState) => state.markets.markets.data || []);

  const regions = useMemo(() => {
    return _.uniq(markets.map((m) => m.region).filter((m) => !!m)).map((r) => ({ key: r, text: r }));
  }, [markets]);

  const [saving, setSaving] = useState(false);

  const buildRequest = () => {
    return {
      active: true,
      marketCode,
      marketGroup,
      marketName,
      payerCodes,
      services,
      timeZoneCode: timezone,
      yelpPage,
      region,
    };
  };

  const create = () => {
    setSaving(true);
    createMarket(buildRequest())
      .then((newMarket) => {
        if (newMarket) {
          onCreate();
        }
      })
      .finally(() => setSaving(false));
  };

  useEffect(() => {
    const current = services.map((s) => s.serviceCode);
    const toAdd = _.difference(serviceCodes, current);
    const toDelete = _.difference(current, serviceCodes);

    const newServices = services.slice();
    toAdd.forEach((s) =>
      newServices.push({
        serviceCode: s,
        price: 159,
        visibleToFollowUp: true,
        visibleToPatient: true,
      })
    );

    toDelete.forEach((s) => {
      const index = newServices.findIndex((service) => service.serviceCode === s);
      newServices.splice(index, 1);
    });

    setServices(newServices);
    // Intentional to not have services in dependencies (re-entrant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCodes]);

  return (
    <Stack tokens={{ childrenGap: 6 }}>
      <TextField label="Name" required={true} value={marketName} onChange={(e, v) => setMarketName(v!)} />
      <TextField
        label="Code"
        placeholder="2 alpha numeric characters"
        required={true}
        value={marketCode}
        minLength={2}
        maxLength={2}
        onChange={(e, v) => setMarketCode(v!.toUpperCase())}
      />
      <TextField
        label="Group"
        placeholder="2 alpha numeric characters"
        required={true}
        value={marketGroup}
        minLength={2}
        maxLength={2}
        onChange={(e, v) => setMarketGroup(v!.toUpperCase())}
      />
      <Dropdown label="Region" required={true} onChange={(e, v) => setRegion(v!.key as string)} selectedKey={region} options={regions} />
      <TimezoneSelector label="Timezone" required={true} value={timezone} onChange={(v) => setTimezone(v)} />
      <MultiSelector
        label="Services"
        placeholder="Select services"
        required={true}
        items={allServices.map((m) => ({ key: m.code, text: m.name }))}
        selectedKeys={serviceCodes}
        onChange={(v) => setServiceCodes(v)}
      />
      {services.map((service, i) => (
        <Stack horizontal tokens={{ childrenGap: 6 }} key={i}>
          <TextField disabled={true} value={service.serviceCode} label="Service Code" />
          <TextField value={'' + service.price} label="Price" onChange={(e, v) => setServices(updateArray(services, i, (u) => (u.price = parseInt(v!))))} />
          <Toggle checked={service.visibleToPatient} label="Patient" onChange={(e, c) => setServices(updateArray(services, i, (u) => (u.visibleToPatient = c!)))} />
          <Toggle checked={service.visibleToFollowUp} label="Provider" onChange={(e, c) => setServices(updateArray(services, i, (u) => (u.visibleToFollowUp = c!)))} />
        </Stack>
      ))}
      <MultiSelector
        label="Payers"
        placeholder="Select payers"
        required={true}
        items={allPayers.map((m) => ({ key: m.eligiblePayerCode, text: `${m.shortName} (${m.eligiblePayerCode})` }))}
        selectedKeys={payerCodes}
        onChange={(v) => setPayerCodes(v)}
      />
      <TextField label="Yelp Review Page" required={true} value={yelpPage} onChange={(e, v) => setYelpPage(v!)} />
      <Stack.Item align="end" styles={{ root: { paddingTop: 40 } }}>
        <PrimaryButton disabled={saving} text="Create" onClick={create} />
      </Stack.Item>
      <div>
        <Label>Diagnostics</Label>
        <pre style={{ margin: 0, padding: 12, overflow: 'auto', background: '#eaeaea' }}>{JSON.stringify(buildRequest(), null, 2)}</pre>
      </div>
    </Stack>
  );
};

const MarketDefinitions = ({ hideInactive }: { hideInactive: boolean }) => {
  const markets = useSelector((state: RootState) => state.markets.allMarkets);
  const navigate = useNavigate();

  const columns: IColumn[] = [
    {
      key: 'region',
      name: 'Region',
      fieldName: 'region',
      minWidth: 120,
      maxWidth: 120,
    },
    {
      key: 'groupCode',
      name: 'Group',
      fieldName: 'groupCode',
      minWidth: 50,
      maxWidth: 50,
    },
    {
      key: 'code',
      name: 'Code',
      fieldName: 'code',
      minWidth: 40,
      maxWidth: 40,
      onRender: (item: Market) => <Link onClick={() => navigate(`${ROUTES.markets}/${item.code}`)}>{item.code}</Link>,
    },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'timeZone',
      name: 'Time Zone',
      fieldName: 'timeZone',
      minWidth: 160,
      maxWidth: 160,
    },
    {
      key: 'yelpPage',
      name: 'Yelp',
      fieldName: 'yelpPage',
      minWidth: 130,
      maxWidth: 300,
    },
    {
      key: 'priority',
      name: 'Priority',
      fieldName: 'priority',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: 'count',
      name: 'Services',
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: Market) => item.serviceOfferingMarkets.length,
    },
    {
      key: 'active',
      name: 'Active',
      fieldName: 'active',
      minWidth: 60,
      maxWidth: 60,
      onRender: (item: Market) => <Toggle disabled={true} checked={item.active} />,
    },
    {
      key: 'actions',
      name: '',
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: Market) => {
        const onDeleteClick = () => {
          OkCancelDialog.show({
            title: `Remove Market (${item.code}) ${item.name}`,
            subText: 'Only markets with no visits can be removed. This operation cannot be undone - are you sure you want to continue?',
            onOk: () => deleteMarket(item.code).then((ok) => ok && navigate(0)),
          });
        };
        return (
          <Stack horizontal={true} tokens={{ childrenGap: 10 }}>
            <IconButton iconProps={{ iconName: 'Delete' }} onClick={onDeleteClick} />
          </Stack>
        );
      },
    },
  ];

  return (
    <ShimmeredDetailsList
      enableShimmer={markets.isFetching}
      shimmerLines={8}
      compact={true}
      selectionMode={SelectionMode.none}
      constrainMode={ConstrainMode.horizontalConstrained}
      checkboxVisibility={CheckboxVisibility.hidden}
      items={(markets.data || []).filter((m) => !hideInactive || m.active)}
      setKey="set"
      columns={columns}
      onShouldVirtualize={() => false}
    />
  );
};
